import * as React from "react"
import { useOrganizationPlanFeaturesQuery } from "@modules/organization/queries.generated"
import { usePlatformLimitSummariesQuery } from "@modules/organization/queries.generated"
import { useWorkspaceDetailsShortcuts } from "@modules/organization/builds/hooks/useWorkspaceDetailsShortcuts"
import { useFlags } from "@modules/featureFlags"

const TIMEOUT_THRESHOLD = 5000
const POLLING_INTERVAL = 999

export function useCustomDomainsUsage(organizationId: string) {
  const { flags } = useFlags()
  const [isRecalculating, setIsRecalculating] = React.useState(false)
  const [message, setMessage] = React.useState<React.ReactNode>(``)

  const workspace = useWorkspaceDetailsShortcuts(organizationId)

  const featureInPlan = useOrganizationPlanFeaturesQuery({
    variables: { id: organizationId },
  })

  // gets all available slots of the plan
  const all =
    featureInPlan.data?.organizationDetails?.billing?.plan?.baseFeatures
      ?.domains

  const platformUsage = usePlatformLimitSummariesQuery({
    variables: { workspaceId: organizationId },
  })

  const featureUsage = platformUsage.data?.platformLimitSummaries?.find(
    item => item.name === `CUSTOM_DOMAINS`
  )

  // gets all ocupied slots at the moment
  const used = featureUsage?.workspace?.count

  React.useEffect(() => {
    // when the updated usage data comes, it updates the message
    setMessage(getMessage())
    // stops polling
    platformUsage.stopPolling()
    // and terminate recalculating state
    setIsRecalculating(false)
  }, [all, used])

  // every successful custom domain action, such as adding a new custom domain
  // or removing existing one triggers the recalculate method
  const recalculate = () => {
    // starts polling platformLimitsSummaries data expecting new values (see useEffect above)
    platformUsage.startPolling(POLLING_INTERVAL)
    // and initiate the recalculating state
    setIsRecalculating(true)

    // in cases when the plaatformLimitsSummaries data does not change, e.g. when the secondary domain was removed
    // it manually stop polling and terminating recalculation state after TIMEOUT_THRESHOLD
    const clean = setTimeout(() => {
      platformUsage.stopPolling()
      setIsRecalculating(false)
      clearTimeout(clean)
    }, TIMEOUT_THRESHOLD)
  }

  const getMessage = () =>
    typeof all === `number` && typeof used === `number` && all
      ? `${used} / ${all} used in ${workspace.name}`
      : ``

  const isExhausted = () =>
    typeof all === `number` &&
    typeof used === `number` &&
    all &&
    used &&
    all - used <= 0

  const isUnlimited = () => all === -1 || flags.pricingFall2022

  return {
    all,
    used,
    isExhausted: isExhausted(),
    isUnlimited: isUnlimited(),
    message,
    recalculate,
    isRecalculating,
  }
}
